import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import {
  CustomSectionTitle,
  CustomTable,
  CustomColumnDef,
  CustomRenderCellWithDash,
  CustomTruncateText,
  MobileTable,
  CustomChip,
  CustomButton,
  CustomModal,
  CustomSingleSelect,
  CustomSummaryCard,
  colors,
} from "@ippon/shared-components";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import ipponApi from "@/config/axios";
import type { AppDispatch } from "@/store";
// 旧 deletePropertySections は Unit API (deleteUnit) ベースの薄い stub に差し替え。
import { deleteUnit } from "@/store/unitSlice";
const deletePropertySections = (args: { property_section_ids: string[] }) =>
  Promise.all(
    args.property_section_ids.map((id) => deleteUnit({ unit_id: id })),
  ) as any;
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { Can } from "@/components/auth/Can";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import { getApiErrorType, type ApiErrorType } from "@/utils/apiErrorType";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";
import type { PropertySection } from "@/types/propertySection";
import {
  toPrivateAreaRow,
  toAncillaryRow,
  equipmentToAncillaryRow,
  calcRentRollKpi,
  formatCurrency,
} from "@/utils/propertySection";
import UnitCreateModal from "@/components/PropertyDetail/Unit/UnitCreateModal";
// 区画詳細ページの tab/subtab は数値 index ベース (resolveTabId が Number() で
// 解釈する)。文字列キー (?tab=tenant 等) を渡すと parser が NaN を吐き
// 「strictlyInvalidTabQuery」判定で 404 表示になるので、必ずこの定数経由で組む。
import {
  PRIVATE_AREA_TENANT_CONTRACT_TAB_SEARCH,
  PRIVATE_AREA_SECTION_TAB_SEARCH,
  PRIVATE_AREA_TAB_INDICES,
  DEFAULT_PRIVATE_AREA_TENANT_SUBTAB_INDEX,
} from "@/pages/properties/PrivateArea/Detail/tabConfig";
import PrivateAreaDetailDrawer from "@/components/PropertyDetail/Budget/PrivateAreaDetailDrawer";
import {
  formatContractPeriod,
  formatMonthlyAndUnitPrice,
  formatTsuboArea,
  formatTsuboUnitPrice,
} from "./display";
// Unit API → 旧 PropertySection / EquipmentRecord shape adapter
import {
  unitToPropertySection,
  unitToEquipmentRecord,
} from "@/utils/unitPropertySectionAdapter";
// EquipmentRecord 型は Unit に統合済み。最低限のシムで型を満たす。
type EquipmentRecord = {
  equipment_id: string;
  property_id: string;
  name: string;
  category?: string;
  status?: string;
  is_revenue_generating?: boolean;
  revenue_type?: string;
  revenue_status?: string;
  monthly_income?: number;
  contractor_name?: string;
  contract_start_date?: string;
  contract_end_date?: string;
};

const BaseURL = import.meta.env.VITE_API_URL;
const RENT_ROLL_CACHE_PREFIX = "rent-roll-cache:";

type RentRollSessionCache = {
  paRaw: PropertySection[];
  anRaw: PropertySection[];
  equipmentAncillary: EquipmentRecord[];
};

const readRentRollSessionCache = (
  propertyId: string,
): RentRollSessionCache | null => {
  if (!propertyId || typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(
      `${RENT_ROLL_CACHE_PREFIX}${propertyId}`,
    );
    return raw ? (JSON.parse(raw) as RentRollSessionCache) : null;
  } catch {
    return null;
  }
};

const writeRentRollSessionCache = (
  propertyId: string,
  cache: RentRollSessionCache,
) => {
  if (!propertyId || typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      `${RENT_ROLL_CACHE_PREFIX}${propertyId}`,
      JSON.stringify(cache),
    );
  } catch {
    // sessionStorage may be unavailable or full; in-memory state still works.
  }
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface RentRollProps {
  propertyId: string;
  propertyName: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const RentRoll: React.FC<RentRollProps> = ({ propertyId, propertyName }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [subTab, setSubTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [initialLoadError, setInitialLoadError] = useState<ApiErrorType | null>(
    null,
  );

  // 名称クリックは直接ページ遷移せず、まず区画プレビュードロワーを開く。
  // (展開ボタンで従来の単独ページ /units/:id へ)
  const [unitPreview, setUnitPreview] = useState<{
    id: string;
    tab: number;
    subtab?: number;
    expandSearch: string;
    name?: string;
  } | null>(null);
  const openUnitPreview = useCallback(
    (
      id: string,
      tab: number,
      expandSearch: string,
      name?: string,
      subtab?: number,
    ) => {
      if (!id) return;
      setUnitPreview({ id, tab, subtab, expandSearch, name });
    },
    [],
  );

  // ── ツールバー用 state ──
  const [perPagePA, setPerPagePA] = useState<number>(50);
  const [perPageAN, setPerPageAN] = useState<number>(50);
  const [selectedPAIds, setSelectedPAIds] = useState<Set<string>>(new Set());
  const [selectedANIds, setSelectedANIds] = useState<Set<string>>(new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // 旧 create ページは廃止 → UnitCreateModal を開いて登録
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const initialSessionCache = useMemo(
    () => readRentRollSessionCache(propertyId),
    [propertyId],
  );

  const { control: perPageControl } = useForm<{
    perPagePA: number;
    perPageAN: number;
  }>({ defaultValues: { perPagePA: 50, perPageAN: 50 } });

  const perPageOptions = useMemo(
    () => [
      { value: 10, label: "10件" },
      { value: 25, label: "25件" },
      { value: 50, label: "50件" },
      { value: 100, label: "100件" },
    ],
    [],
  );

  // レントロール専用のローカル state
  // ※ Redux の list.data を使うと他タブ（専有部一覧・付帯設備一覧・修繕等）と
  //   フェッチが競合して list.data を上書きし合うため、独立して保持する
  const [paRaw, setPaRaw] = useState<PropertySection[]>(
    () => initialSessionCache?.paRaw || [],
  );
  const [anRaw, setAnRaw] = useState<PropertySection[]>(
    () => initialSessionCache?.anRaw || [],
  );
  // 設備マスタの収益発生レコード (機械式駐車場・屋上太陽光等)
  const [equipmentAncillary, setEquipmentAncillary] = useState<
    EquipmentRecord[]
  >(() => initialSessionCache?.equipmentAncillary || []);
  const [loaded, setLoaded] = useState<boolean>(() =>
    Boolean(initialSessionCache),
  );
  const hasRentRollCache =
    loaded ||
    paRaw.length > 0 ||
    anRaw.length > 0 ||
    equipmentAncillary.length > 0;
  const hasRentRollCacheRef = useRef(hasRentRollCache);

  useEffect(() => {
    hasRentRollCacheRef.current = hasRentRollCache;
  }, [hasRentRollCache]);

  useEffect(() => {
    const cache = readRentRollSessionCache(propertyId);
    if (!cache) return;
    setPaRaw(cache.paRaw || []);
    setAnRaw(cache.anRaw || []);
    setEquipmentAncillary(cache.equipmentAncillary || []);
    setLoaded(true);
  }, [propertyId]);

  useEffect(() => {
    if (!loaded) return;
    writeRentRollSessionCache(propertyId, {
      paRaw,
      anRaw,
      equipmentAncillary,
    });
  }, [anRaw, equipmentAncillary, loaded, paRaw, propertyId]);

  const fetchAll = useCallback(
    async (cancelledRef?: { current: boolean }) => {
      if (!propertyId) {
        setInitialLoadError("notFound");
        return;
      }
      setInitialLoadError(null);
      setLoading(true);
      try {
        // BE 側で revenue_type=stock_rent 指定 → GSI2 経由で収益区画を一括抽出 (段階1)。
        //   レントロールは「収益あり」のみが対象なので、BE で先に絞ってから
        //   FE 側では area_type で 2 タブ (貸室 / その他収入) に振り分ける。
        // 契約者名 / 賃料 / 期間は Contracts テーブル側に保存されているため、
        // 同じ物件配下の active 契約も並列取得して unit.current_contract_id で join。
        const [unitsRes, contractsRes] = await Promise.all([
          ipponApi.get(`${BaseURL}/api/v1/units`, {
            params: { property_id: propertyId, revenue_type: "stock_rent" },
          }),
          ipponApi.get(`${BaseURL}/api/v1/contracts`, {
            params: { property_id: propertyId, status: "active" },
          }),
        ]);
        if (cancelledRef?.current) return;
        const stockUnits = (unitsRes.data?.data?.items || []) as any[];
        const activeContracts = (contractsRes.data?.data?.items || []) as any[];

        // unit_id → 最新 active 契約 のマップ (created_at の新しいものを採用)。
        // contract_id → 契約 のマップも同時に作り、unit.current_contract_id 指定時はそちらを優先。
        const contractsByUnitId: Record<string, any> = {};
        const contractsById: Record<string, any> = {};
        for (const c of activeContracts) {
          if (c?.contract_id) contractsById[c.contract_id] = c;
          const uid = c?.unit_id;
          if (!uid) continue;
          const prev = contractsByUnitId[uid];
          if (
            !prev ||
            new Date(c.created_at ?? 0).getTime() >
              new Date(prev.created_at ?? 0).getTime()
          ) {
            contractsByUnitId[uid] = c;
          }
        }

        const pickContract = (u: any) => {
          const byId = u?.current_contract_id
            ? contractsById[u.current_contract_id]
            : null;
          return byId || contractsByUnitId[u?.unit_id] || null;
        };

        // 貸室タブ: area_type=private
        const paData = stockUnits
          .filter((u) => u.area_type === "private")
          .map((u) =>
            unitToPropertySection(u as any, pickContract(u)),
          ) as unknown as PropertySection[];

        // その他収入タブ (PropertySection 側): area_type ∈ {common, land, other}
        const anData = stockUnits
          .filter(
            (u) => u.area_type !== "private" && u.area_type !== "facility",
          )
          .map((u) =>
            unitToPropertySection(u as any, pickContract(u)),
          ) as unknown as PropertySection[];

        // その他収入タブ (Equipment 側): area_type=facility
        const eqAncillary = stockUnits
          .filter((u) => u.area_type === "facility")
          .map((u) =>
            unitToEquipmentRecord(u as any, pickContract(u)),
          ) as EquipmentRecord[];
        setPaRaw(paData);
        setAnRaw(anData);
        setEquipmentAncillary(eqAncillary);
        setLoaded(true);
      } catch (err) {
        console.error("レントロール取得エラー:", err);
        if (!cancelledRef?.current) {
          const errorType = getApiErrorType(err);
          if (errorType === "notFound" || !hasRentRollCacheRef.current) {
            setPaRaw([]);
            setAnRaw([]);
            setEquipmentAncillary([]);
          }
          setInitialLoadError(errorType);
        }
      } finally {
        if (!cancelledRef?.current) setLoading(false);
      }
    },
    [propertyId],
  );

  useEffect(() => {
    const cancelledRef = { current: false };
    fetchAll(cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchAll, reloadKey]);

  // ── 削除ハンドラ ──
  const handleOpenDeleteModal = () => {
    const ids = subTab === 0 ? selectedPAIds : selectedANIds;
    if (ids.size === 0) {
      showToast({
        type: "warning",
        message: "削除する項目を選択してください",
      });
      return;
    }
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    setDeleteModalOpen(false);
    setIsDeleting(true);
    try {
      const allIds = Array.from(subTab === 0 ? selectedPAIds : selectedANIds);
      // 設備マスタ由来 (equipment:xxx) は別 API のためここでは扱わない
      const equipmentIds = allIds.filter((id) => id.startsWith("equipment:"));
      const sectionIds = allIds.filter((id) => !id.startsWith("equipment:"));
      if (sectionIds.length > 0) {
        await dispatch(
          deletePropertySections({ property_section_ids: sectionIds }),
        ).unwrap();
      }
      if (subTab === 0) setSelectedPAIds(new Set());
      else setSelectedANIds(new Set());
      setReloadKey((k) => k + 1);
      const skippedMsg =
        equipmentIds.length > 0
          ? ` (設備 ${equipmentIds.length} 件は設備管理画面から削除してください)`
          : "";
      showToast({
        type: "success",
        message: `${sectionIds.length}件を削除しました${skippedMsg}`,
      });
    } catch (e) {
      console.error(e);
      showToast({ type: "error", message: "削除に失敗しました" });
    } finally {
      setIsDeleting(false);
    }
  };

  // 旧 create ページは廃止。Unit 統合のため、UnitCreateModal を開く。
  const handleCreate = () => {
    setCreateModalOpen(true);
  };
  // 貸室登録では場所区分を専有部で初期表示する。

  // ── 表示用 Row に変換（共通ユーティリティ使用） ──
  const privateAreas = useMemo(() => paRaw.map(toPrivateAreaRow), [paRaw]);
  // 付帯収益: PropertySection (敷地/共用部の自販機・駐車場等)
  //         + Equipment マスタ (機械式駐車場・屋上太陽光等) を統合
  const ancillaries = useMemo(
    () => [
      ...anRaw.map(toAncillaryRow),
      ...equipmentAncillary.map(equipmentToAncillaryRow),
    ],
    [anRaw, equipmentAncillary],
  );

  // ── KPI 計算（共益費含む・vacant デフォルト修正済み） ──
  const kpi = useMemo(
    () => calcRentRollKpi(privateAreas, ancillaries),
    [privateAreas, ancillaries],
  );

  // 「YYYY年M月D日」形式 (現時点のスナップショット)
  const snapshotDate = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  }, []);

  // ── 専有部カラム ──
  const privateAreaColumns: CustomColumnDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "名称",
        flex: 1.2,
        sortable: true,
        renderCell: ({ row, value }) => (
          <Box
            component="span"
            sx={{
              color: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "14px",
              "&:hover": { color: "#0b9dbd" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              // レントロール → 区画プレビュー(テナント管理 ＞ 契約情報タブ)。展開で単独ページへ(G-3)
              openUnitPreview(
                String(row.id),
                PRIVATE_AREA_TAB_INDICES.TENANT,
                PRIVATE_AREA_TENANT_CONTRACT_TAB_SEARCH,
                String(row.name || ""),
                DEFAULT_PRIVATE_AREA_TENANT_SUBTAB_INDEX,
              );
            }}
          >
            <CustomTruncateText text={String(value || "")} />
          </Box>
        ),
      },
      {
        field: "occupancyStatus",
        headerName: "ステータス",
        flex: 0.8,
        renderCell: ({ value }) => (
          <CustomChip
            label={value === "occupied" ? "入居中" : "空室"}
            backgroundColor={value === "occupied" ? "#E8F5E9" : "#F5F5F5"}
            color={value === "occupied" ? "#2E7D32" : "#616161"}
          />
        ),
      },
      {
        field: "floor",
        headerName: "階",
        flex: 0.5,
        renderCell: CustomRenderCellWithDash,
      },
      {
        field: "areaTsubo",
        headerName: "面積",
        flex: 0.8,
        sortable: true,
        renderCell: ({ value }) => (
          <Typography sx={{ fontSize: "14px" }}>
            {formatTsuboArea(Number(value || 0))}
          </Typography>
        ),
      },
      {
        field: "contractorName",
        headerName: "契約者名",
        flex: 1.2,
        sortable: true,
        renderCell: CustomRenderCellWithDash,
      },
      {
        field: "monthlyRent",
        headerName: "賃料（月額 / 坪単価）",
        flex: 1.5,
        sortable: true,
        renderCell: ({ row, value }) => (
          <Typography
            noWrap
            sx={{ fontSize: "14px", fontVariantNumeric: "tabular-nums" }}
          >
            {formatMonthlyAndUnitPrice(
              Number(value || 0),
              Number(row.rentUnitPrice || 0),
            )}
          </Typography>
        ),
      },
      {
        field: "maintenanceFee",
        headerName: "共益費（月額 / 坪単価）",
        flex: 1.5,
        sortable: true,
        renderCell: ({ row, value }) => (
          <Typography
            noWrap
            sx={{ fontSize: "14px", fontVariantNumeric: "tabular-nums" }}
          >
            {formatMonthlyAndUnitPrice(
              Number(value || 0),
              Number(row.maintenanceFeeUnitPrice || 0),
            )}
          </Typography>
        ),
      },
      {
        field: "totalMonthlyRent",
        headerName: "合計（月額 / 坪単価）",
        flex: 1.5,
        sortable: true,
        valueGetter: ({ row }) =>
          Number(row.monthlyRent || 0) + Number(row.maintenanceFee || 0),
        renderCell: ({ row, value }) => (
          <Typography
            noWrap
            sx={{ fontSize: "14px", fontVariantNumeric: "tabular-nums" }}
          >
            {formatMonthlyAndUnitPrice(
              Number(value || 0),
              Number(row.totalUnitPrice || 0),
            )}
          </Typography>
        ),
      },
      {
        field: "contractStartDate",
        headerName: "契約期間",
        flex: 1.5,
        sortable: true,
        renderCell: ({ row }) => (
          <Typography noWrap sx={{ fontSize: "14px" }}>
            {formatContractPeriod(
              String(row.contractStartDate || ""),
              String(row.contractEndDate || ""),
            )}
          </Typography>
        ),
      },
    ],
    [navigate, propertyId, propertyName],
  );

  // ── 付帯設備カラム ──
  const ancillaryColumns: CustomColumnDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "名称",
        flex: 1.2,
        sortable: true,
        renderCell: ({ row, value }) => (
          <Box
            component="span"
            sx={{
              color: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "14px",
              "&:hover": { color: "#0b9dbd" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              const id = String(row.id || "");
              // 設備マスタ由来 (id="equipment:xxx") は区画プレビュー(区画詳細タブ)
              if (id.startsWith("equipment:")) {
                openUnitPreview(
                  id.slice("equipment:".length),
                  PRIVATE_AREA_TAB_INDICES.SECTION,
                  PRIVATE_AREA_SECTION_TAB_SEARCH,
                  String(row.name || ""),
                );
                return;
              }
              // PropertySection 由来は区画プレビュー(テナント管理 ＞ 契約情報タブ) (G-3)
              openUnitPreview(
                id,
                PRIVATE_AREA_TAB_INDICES.TENANT,
                PRIVATE_AREA_TENANT_CONTRACT_TAB_SEARCH,
                String(row.name || ""),
                DEFAULT_PRIVATE_AREA_TENANT_SUBTAB_INDEX,
              );
            }}
          >
            <CustomTruncateText text={String(value || "")} />
          </Box>
        ),
      },
      {
        field: "occupancyStatus",
        headerName: "ステータス",
        flex: 1,
        renderCell: ({ value }) => (
          <CustomChip
            label={value === "occupied" ? "契約中" : "空室"}
            backgroundColor={value === "occupied" ? "#E8F5E9" : "#F5F5F5"}
            color={value === "occupied" ? "#2E7D32" : "#616161"}
          />
        ),
      },
      {
        field: "sectionCategory",
        headerName: "種別",
        flex: 1,
        renderCell: CustomRenderCellWithDash,
      },
      {
        field: "contractorName",
        headerName: "契約者名",
        flex: 1.5,
        sortable: true,
        renderCell: CustomRenderCellWithDash,
      },
      {
        field: "monthlyIncome",
        headerName: "月額",
        flex: 1,
        sortable: true,
        renderCell: ({ value }) => (
          <Typography
            sx={{ fontSize: "14px", fontVariantNumeric: "tabular-nums" }}
          >
            {formatCurrency(Number(value || 0))}
          </Typography>
        ),
      },
      {
        field: "pricingType",
        headerName: "料金体系",
        flex: 1,
        renderCell: ({ value }) => (
          <Typography sx={{ fontSize: "14px" }}>
            {value === "unit_quantity" ? "単価 × 数量" : "固定月額"}
          </Typography>
        ),
      },
      {
        field: "unitPrice",
        headerName: "単価",
        flex: 1,
        renderCell: ({ row, value }) => (
          <Typography
            sx={{ fontSize: "14px", fontVariantNumeric: "tabular-nums" }}
          >
            {row.pricingType === "unit_quantity"
              ? formatCurrency(Number(value || 0))
              : "-"}
          </Typography>
        ),
      },
      {
        field: "quantity",
        headerName: "数量",
        flex: 0.8,
        renderCell: ({ row, value }) => (
          <Typography sx={{ fontSize: "14px" }}>
            {row.pricingType === "unit_quantity"
              ? `${Number(value || 0).toLocaleString("ja-JP")}${row.quantityUnit || ""}`
              : "一式"}
          </Typography>
        ),
      },
      {
        field: "contractStartDate",
        headerName: "契約期間",
        flex: 1.5,
        sortable: true,
        renderCell: ({ row }) => (
          <Typography noWrap sx={{ fontSize: "14px" }}>
            {formatContractPeriod(
              String(row.contractStartDate || ""),
              String(row.contractEndDate || ""),
            )}
          </Typography>
        ),
      },
    ],
    [navigate, propertyId, propertyName],
  );

  // ── サマリーカード ──
  const SummaryCard = CustomSummaryCard;

  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasRentRollCache,
    dedupeKey: `rent-roll:${propertyId}`,
  });

  if (shouldShowInitialLoadError(initialLoadError, hasRentRollCache)) {
    return (
      <InitialLoadError
        errorType={initialLoadError}
        onRetry={() => fetchAll()}
      />
    );
  }

  return (
    <Box mb={3}>
      <Box sx={{ mb: 2 }}>
        <CustomSectionTitle
          title={`レントロール（${snapshotDate}時点）`}
          addBorder
        />
      </Box>

      {/* サマリーカード (常時 5 枚固定) */}
      {(() => {
        const paTotal = privateAreas.length;
        const paActive = paTotal - kpi.vacantCount;
        const paRate = paTotal > 0 ? (paActive / paTotal) * 100 : 0;

        const anActive = ancillaries.filter(
          (a) => a.occupancyStatus === "occupied",
        ).length;
        const anTotal = ancillaries.length;
        const anRate = anTotal > 0 ? (anActive / anTotal) * 100 : 0;

        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              // 金額が長いカードだけ内容に追従して広がる (均等割りにしない)。
              // 各カードは内容幅、入り切らなければ次行へ折返す。
              gap: 1,
              mb: 1,
              "& > *": { flex: "0 0 auto", minWidth: "160px" },
              "& .summary-card-value": { whiteSpace: "nowrap" },
            }}
          >
            <SummaryCard
              sx={{ backgroundColor: "#F9FCFE" }}
              label="月額収入"
              value={`¥${kpi.totalMonthly.toLocaleString("ja-JP")}`}
              loading={loading}
              loadingDelayMs={150}
            />
            <SummaryCard
              label="月額賃料"
              value={`¥${kpi.totalRent.toLocaleString("ja-JP")}`}
              loading={loading}
              loadingDelayMs={150}
            />
            <SummaryCard
              label="月額共益費"
              value={`¥${kpi.totalMaintenanceFee.toLocaleString("ja-JP")}`}
              loading={loading}
              loadingDelayMs={150}
            />
            <SummaryCard
              label="平均坪単価"
              value={formatTsuboUnitPrice(kpi.weightedUnitPrice)}
              sub="共益費込"
              loading={loading}
              loadingDelayMs={150}
            />
            <SummaryCard
              label="貸室契約率"
              value={
                paTotal > 0 ? (
                  <>
                    {paActive}
                    <Box
                      component="span"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#999",
                        ml: 0.5,
                      }}
                    >
                      / {paTotal}室
                    </Box>
                  </>
                ) : (
                  "-"
                )
              }
              progress={paRate}
              progressLabel={`${
                paRate === 0 || paRate === 100
                  ? paRate.toFixed(0)
                  : paRate.toFixed(1)
              }%`}
              loading={loading}
              loadingDelayMs={150}
            />
            <SummaryCard
              label="その他収入"
              value={`¥${kpi.totalAncillary.toLocaleString("ja-JP")}`}
              loading={loading}
              loadingDelayMs={150}
            />
            <SummaryCard
              label="その他収入契約率"
              value={
                anTotal > 0 ? (
                  <>
                    {anActive}
                    <Box
                      component="span"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#999",
                        ml: 0.5,
                      }}
                    >
                      / {anTotal}件
                    </Box>
                  </>
                ) : (
                  "-"
                )
              }
              progress={anRate}
              progressLabel={`${
                anRate === 0 || anRate === 100
                  ? anRate.toFixed(0)
                  : anRate.toFixed(1)
              }%`}
              loading={loading}
              loadingDelayMs={150}
            />
          </Box>
        );
      })()}

      {/* サブタブ */}
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mb: 1 }}>
        <Tabs
          value={subTab}
          onChange={(_, v) => setSubTab(v)}
          sx={{
            minHeight: "40px",
            "& .MuiTabs-indicator": {
              backgroundColor: "#0B9DBD",
              height: "2px",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontFamily: '"Noto Sans JP"',
              fontSize: "14px",
              fontWeight: 600,
              minHeight: "40px !important",
              paddingTop: "8px !important",
              paddingBottom: "1px !important",
              paddingLeft: "16px",
              paddingRight: "16px",
              color: "#989898",
              "&:hover": {
                color: "#0B9DBD",
                opacity: 1,
              },
              "&:hover .rent-roll-tab-count": {
                backgroundColor: "rgba(11,157,189,0.1)",
                color: "#0B9DBD",
              },
              "&.Mui-selected": { color: "#0B9DBD" },
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                貸室
                <Box
                  component="span"
                  className="rent-roll-tab-count"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "10px",
                    px: 1,
                    fontSize: "11px",
                    fontWeight: 600,
                    backgroundColor:
                      subTab === 0 ? "rgba(11,157,189,0.1)" : "#F5F5F5",
                    color: subTab === 0 ? "#0B9DBD" : "#989898",
                  }}
                >
                  {privateAreas.length}
                </Box>
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                その他収入
                <Box
                  component="span"
                  className="rent-roll-tab-count"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "10px",
                    px: 1,
                    fontSize: "11px",
                    fontWeight: 600,
                    backgroundColor:
                      subTab === 1 ? "rgba(11,157,189,0.1)" : "#F5F5F5",
                    color: subTab === 1 ? "#0B9DBD" : "#989898",
                  }}
                >
                  {ancillaries.length}
                </Box>
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* ツールバー */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 1.25,
          mb: 1,
        }}
      >
        <Stack direction="row" gap={1} alignItems="center">
          <Typography
            sx={{
              whiteSpace: "nowrap",
              fontWeight: 700,
              fontSize: "14px",
              color: "#3e3e3e",
            }}
          >
            表示件数
          </Typography>
          <Box sx={{ minWidth: 70 }}>
            <CustomSingleSelect
              label=""
              name={subTab === 0 ? "perPagePA" : "perPageAN"}
              placeholder=""
              control={perPageControl}
              errors={{}}
              inputWidth="80px"
              inputWidthSp="100%"
              options={perPageOptions}
              hasSpacing={false}
              onChangeValue={(v) => {
                if (subTab === 0) setPerPagePA(Number(v));
                else setPerPageAN(Number(v));
              }}
            />
          </Box>
        </Stack>

        <CustomButton
          label={subTab === 0 ? "貸室登録" : "その他収入登録"}
          startIcon={<AddIcon sx={{ fontSize: 20 }} />}
          onClick={handleCreate}
          sx={{
            justifyContent: "center",
            height: 36,
            borderRadius: "10px",
            px: 2,
            backgroundColor: "#0b9dbd",
            color: "#ffffff",
            fontFamily: '"Noto Sans JP"',
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "16.8px",
            "&:hover": { backgroundColor: "#0a8aa7" },
          }}
        />

        <Can action="unit:delete">
          <CustomButton
            label="削除"
            buttonCategory="delete"
            onClick={handleOpenDeleteModal}
            disabled={
              isDeleting ||
              (subTab === 0
                ? selectedPAIds.size === 0
                : selectedANIds.size === 0)
            }
            spIcon={true}
          />
        </Can>
      </Box>

      {/* テーブル: ローディング中はヘッダー (列名) は表示したまま body だけ
          CustomTable 内蔵の CustomLoadingOverlay を出す。 */}
      {subTab === 0 ? (
        isMobile ? (
          <MobileTable
            rows={privateAreas}
            columns={privateAreaColumns}
            noDataMessage="区画が登録されていません"
            noDataIcon={
              <MeetingRoomOutlinedIcon
                sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
              />
            }
            handlePropertyClick={(row) =>
              openUnitPreview(
                String(row.id),
                PRIVATE_AREA_TAB_INDICES.TENANT,
                PRIVATE_AREA_TENANT_CONTRACT_TAB_SEARCH,
                String(row.name || ""),
                DEFAULT_PRIVATE_AREA_TENANT_SUBTAB_INDEX,
              )
            }
          />
        ) : (
          <CustomTable
            key="private-area-table"
            rows={privateAreas}
            columns={privateAreaColumns}
            outlined={false}
            minHeight="auto"
            initialSort={{ field: "name", direction: "asc" }}
            enableCheckboxSelection={true}
            onRowSelection={(ids: string[]) => setSelectedPAIds(new Set(ids))}
            pageSize={perPagePA}
            isLoading={loading}
            noDataMessage="区画が登録されていません"
            noDataIcon={
              <MeetingRoomOutlinedIcon
                sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
              />
            }
          />
        )
      ) : isMobile ? (
        <MobileTable
          rows={ancillaries}
          columns={ancillaryColumns}
          noDataMessage="区画が登録されていません"
          noDataIcon={
            <MeetingRoomOutlinedIcon
              sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
            />
          }
          handlePropertyClick={(row) => {
            const id = String(row.id || "");
            if (id.startsWith("equipment:")) {
              openUnitPreview(
                id.slice("equipment:".length),
                PRIVATE_AREA_TAB_INDICES.SECTION,
                PRIVATE_AREA_SECTION_TAB_SEARCH,
                String(row.name || ""),
              );
              return;
            }
            openUnitPreview(
              id,
              PRIVATE_AREA_TAB_INDICES.TENANT,
              PRIVATE_AREA_TENANT_CONTRACT_TAB_SEARCH,
              String(row.name || ""),
              DEFAULT_PRIVATE_AREA_TENANT_SUBTAB_INDEX,
            );
          }}
        />
      ) : (
        <CustomTable
          key="ancillary-table"
          rows={ancillaries}
          columns={ancillaryColumns}
          outlined={false}
          minHeight="auto"
          initialSort={{ field: "name", direction: "asc" }}
          enableCheckboxSelection={true}
          onRowSelection={(ids: string[]) => setSelectedANIds(new Set(ids))}
          pageSize={perPageAN}
          isLoading={loading}
          noDataMessage="区画が登録されていません"
          noDataIcon={
            <MeetingRoomOutlinedIcon
              sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
            />
          }
        />
      )}

      {/* 削除確認モーダル */}
      <CustomModal
        title="削除の確認"
        openModal={deleteModalOpen}
        handleCloseModal={() => setDeleteModalOpen(false)}
        bodyText=""
        hideCloseButton
        modalType="deleteModal"
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            選択した
            {subTab === 0 ? selectedPAIds.size : selectedANIds.size}
            件を削除してもよろしいでしょうか？
            <br />
            削除されたデータは復元できません。
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <CustomButton
            label="戻る"
            onClick={() => setDeleteModalOpen(false)}
            buttonCategory="cancelModal"
          />
          <CustomButton
            label="削除"
            onClick={handleDelete}
            buttonCategory="deleteModal"
            disabled={isDeleting}
          />
        </Box>
      </CustomModal>
      {/* subTab に応じた area_type で Unit 登録モーダルを開く */}
      <UnitCreateModal
        open={createModalOpen}
        propertyId={propertyId}
        defaultAreaType={subTab === 0 ? "private" : undefined}
        defaultRevenueType="stock_rent"
        titleOverride={subTab === 0 ? "貸室登録" : "その他収入登録"}
        onClose={() => setCreateModalOpen(false)}
        onCreated={(unitId) => {
          setCreateModalOpen(false);
          // 登録後はそのまま Unit 詳細ページへ
          navigate(
            `/properties/${propertyId}/units/${unitId}${PRIVATE_AREA_TENANT_CONTRACT_TAB_SEARCH}`,
            { state: { propertyName } },
          );
        }}
      />

      {/* 名称クリックで開く区画プレビュードロワー (展開で単独ページへ) */}
      <PrivateAreaDetailDrawer
        open={!!unitPreview}
        onClose={() => setUnitPreview(null)}
        propertyId={propertyId}
        privateAreaId={unitPreview?.id ?? null}
        initialTab={unitPreview?.tab}
        initialSubTab={unitPreview?.subtab}
        expandSearch={unitPreview?.expandSearch}
        expandState={
          unitPreview
            ? {
                propertyName,
                propName: propertyName,
                privateAreaName: unitPreview.name,
                fromSource: "rentroll",
              }
            : undefined
        }
      />
    </Box>
  );
};

export default RentRoll;
